import type { Persona, Task, TaskRecurrence } from "../api/generated";
import type { Day } from "./calendarDay";

const DAY_MS = 86_400_000;
const utc = (day: Day) => Date.parse(`${day}T00:00:00Z`);
const daysBetween = (from: Day, to: Day) => Math.round((utc(to) - utc(from)) / DAY_MS);
const format = (day: Day, options: Intl.DateTimeFormatOptions) => new Date(utc(day)).toLocaleDateString(undefined, { ...options, timeZone: "UTC" });

/** When a task is due, relative to today: "Overdue · 2 Nov", "Today", "Tomorrow", "Fri" (this week) or "14 Nov". */
export function dueLabel(dueOn: Day, today: Day): { text: string; tone: "danger" | "warning" | "neutral" } {
  const days = daysBetween(today, dueOn);
  const short = format(dueOn, { day: "numeric", month: "short" });
  if (days < 0) return { text: `Overdue · ${short}`, tone: "danger" };
  if (days === 0) return { text: "Today", tone: "warning" };
  if (days === 1) return { text: "Tomorrow", tone: "neutral" };
  if (days < 7) return { text: format(dueOn, { weekday: "short" }), tone: "neutral" };
  return { text: short, tone: "neutral" };
}

/** 30 → "30m", 120 → "2h", 90 → "1h 30m". */
export function formatEstimate(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

const EVERY_ONE = { day: "Daily", week: "Weekly", month: "Monthly", year: "Yearly" } as const;

/** How often a task repeats: { every: 1, unit: "week" } → "Weekly", { every: 3, unit: "day" } → "Every 3 days". */
export const repeatLabel = ({ every, unit }: TaskRecurrence) => (every === 1 ? EVERY_ONE[unit] : `Every ${every} ${unit}s`);

/** What someone types as an estimate: "45", "45m", "2h", "1.5h", "1h30". Null when empty or unreadable. */
export function parseEstimate(text: string): number | null {
  const match = text.trim().toLowerCase().match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m?)?$/);
  if (!match || (!match[1] && !match[2])) return null;
  const minutes = Math.round(Number(match[1] ?? 0) * 60 + Number(match[2] ?? 0));
  return minutes >= 1 && minutes <= 10_080 ? minutes : null;
}

/** Open tasks, plus ones ticked off today, so a tick can be undone without hunting for it. */
export const isShown = (task: Task, today: Day) =>
  task.completedAt === null || new Date(task.completedAt).toLocaleDateString("en-CA") === today;

export type TaskGroup = { persona: Persona | null; tasks: Task[] };

/** Tasks under each persona (in the order given), then those with none. Empty groups are left out. */
export function groupByPersona(tasks: Task[], personas: Persona[]): TaskGroup[] {
  const groups: TaskGroup[] = [...personas, null].map((persona) => ({ persona, tasks: [] }));
  const byId = new Map(groups.map((group) => [group.persona?.id ?? null, group]));
  for (const task of tasks) (byId.get(task.personaId) ?? groups.at(-1)!).tasks.push(task);
  return groups.filter((group) => group.tasks.length > 0);
}
